import clsx from 'clsx';
import { useDragOperation, useDroppable } from '@dnd-kit/react';
import { CollisionPriority } from '@dnd-kit/abstract';
import { pointerIntersection } from '@dnd-kit/collision';
import { I18n } from 'src/modules/common/components/I18n/I18n';

/** Droppable ids of the cross-page drop zones (matched against in useCrossPageDrag's onDragEnd). */
export const PREV_PAGE_DROP_ZONE_ID = '__vss-prev-page-drop-zone';
export const NEXT_PAGE_DROP_ZONE_ID = '__vss-next-page-drop-zone';

const DROP_ZONE_IDS: Record<'prev' | 'next', string> = {
    prev: PREV_PAGE_DROP_ZONE_ID,
    next: NEXT_PAGE_DROP_ZONE_ID,
};

type Props = {
    direction: 'prev' | 'next';
    messageId: string;
    /** Disabled when there is no adjacent page to move the selection to. */
    disabled: boolean;
};

/**
 * A drop zone rendered in the margin above/below the paginated selection list.
 * Dropping a dragged selection onto it moves that selection to the adjacent page
 * (the actual reorder + navigation happens in ProjectPage's onDragEnd).
 *
 * The zone is always mounted so dnd-kit measures it from the drag start (a droppable
 * revealed mid-drag is not detected). `pointerIntersection` + a high collision priority
 * make the thin zone reliably win as the drop target whenever the pointer is over it,
 * even though the sortable rows sit right next to it. It only becomes visible while a
 * drag is in progress.
 */
export const CrossPageDropZone = ({ direction, messageId, disabled }: Props) => {
    const { source } = useDragOperation();
    const { ref, isDropTarget } = useDroppable({
        id: DROP_ZONE_IDS[direction],
        disabled,
        collisionDetector: pointerIntersection,
        collisionPriority: CollisionPriority.Highest,
    });

    const isDragging = source != null && !disabled;

    return (
        <div
            ref={ref}
            aria-hidden={!isDragging}
            className={clsx('vss-project-page__dropzone', `vss-project-page__dropzone--${direction}`, {
                'vss-project-page__dropzone--visible': isDragging,
                'vss-project-page__dropzone--active': isDropTarget,
            })}
        >
            <span className="vss-project-page__dropzone-label">
                <I18n messageId={messageId}/>
            </span>
        </div>
    );
};
